import React, { useState } from 'react';
import { StyleSheet, TouchableOpacity, View } from 'react-native';
import { Text, TextInput } from 'react-native-paper';
import { useTranslation } from 'react-i18next';
import { NativeStackScreenProps } from '@react-navigation/native-stack';
import { AuthStackParamList } from '../../navigation/types';
import { useAppColors } from '../../hooks/useAppColors';
import { useResetPassword } from '../../hooks/useAuth';
import AuthLayout from './AuthLayout';

type Props = NativeStackScreenProps<AuthStackParamList, 'ResetPassword'>;

export default function ResetPasswordScreen({ navigation, route }: Props) {
  const { t } = useTranslation();
  const colors = useAppColors();
  const resetPassword = useResetPassword();

  const { token } = route.params;
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [secureEntry, setSecureEntry] = useState(true);
  const [done, setDone] = useState(false);
  const [error, setError] = useState('');

  const handleReset = () => {
    setError('');
    if (!newPassword.trim()) {
      setError(t('auth.passwordRequired'));
      return;
    }
    if (newPassword.length < 8) {
      setError(t('auth.passwordMinLength'));
      return;
    }
    if (newPassword !== confirmPassword) {
      setError(t('auth.passwordMismatch'));
      return;
    }
    resetPassword.mutate(
      { newPassword, token },
      {
        onSuccess: () => setDone(true),
        onError: (err) => setError(err.message),
      },
    );
  };

  return (
    <AuthLayout title={t('auth.resetPassword')}>
        {done ? (
          <>
            <Text style={[styles.successText, { color: colors.accent }]}>{t('auth.passwordResetSuccess')}</Text>
            <TouchableOpacity
              style={[styles.button, { backgroundColor: colors.accent }]}
              onPress={() => navigation.navigate('Login')}
            >
              <Text style={[styles.buttonText, { color: colors.accentText }]}>{t('auth.backToSignIn')}</Text>
            </TouchableOpacity>
          </>
        ) : (
          <>
            <TextInput
              label={t('auth.newPassword')}
              value={newPassword}
              onChangeText={setNewPassword}
              secureTextEntry={secureEntry}
              mode="outlined"
              style={styles.input}
              right={
                <TextInput.Icon
                  icon={secureEntry ? 'eye-off' : 'eye'}
                  onPress={() => setSecureEntry(!secureEntry)}
                />
              }
            />

            <TextInput
              label={t('auth.confirmPassword')}
              value={confirmPassword}
              onChangeText={setConfirmPassword}
              secureTextEntry={secureEntry}
              mode="outlined"
              style={styles.input}
            />

            {!!error && <Text style={[styles.error, { color: colors.error }]}>{error}</Text>}

            <TouchableOpacity
              style={[styles.button, { backgroundColor: colors.accent }]}
              onPress={handleReset}
              disabled={resetPassword.isPending}
            >
              <Text style={[styles.buttonText, { color: colors.accentText }]}>
                {resetPassword.isPending ? t('common.loading') : t('auth.resetPassword')}
              </Text>
            </TouchableOpacity>
          </>
        )}
    </AuthLayout>
  );
}

const styles = StyleSheet.create({
  input: {
    marginBottom: 12,
    backgroundColor: 'transparent',
  },
  error: {
    textAlign: 'center',
    marginBottom: 12,
    fontSize: 13,
  },
  successText: {
    fontSize: 18,
    fontWeight: '600',
    textAlign: 'center',
    marginBottom: 24,
  },
  button: {
    marginTop: 8,
    paddingVertical: 14,
    borderRadius: 12,
    alignItems: 'center',
  },
  buttonText: {
    fontSize: 16,
    fontWeight: '600',
  },
});
